import React from 'react';
import MJLogo from './MJLogo';
import { Printer, Download } from 'lucide-react';

/**
 * Professional Invoice Component
 * Matches MJ Enterprises brand colors (amber/slate)
 */
export default function Invoice({ invoiceData, onPrint, onDownload }) {
  // Default invoice data structure
  const data = invoiceData || {
    invoiceNumber: 'MJ/SS108',
    invoiceDate: '10-02-2026',
    dateOfSupply: '10-02-2026',
    poNumber: '302168',
    
    seller: {
      name: 'M&J ENTERPRISES',
      address: '1-B, Rajupalem, Ozili Mandalam, Tirupathi DIST.AP',
      gstin: '37ABMFM1108N1ZZ',
      state: 'AP',
      bankName: 'IDFC FIRST BANK GUDUR',
      accountNumber: '10077978168',
    },
    
    buyer: {
      name: 'SRI BALAJI CASTINGS PVT LTD',
      address: 'No.94,95,102,103, Sipcot Export Promotion Industrial park, Gummidipoondi, Thiruvallur-601201',
      gstin: '33AABCS4985J1Z1',
      state: 'TAMILNADU',
    },
    
    lineItems: [
      {
        serialNo: 1,
        description: 'Silica sand dry',
        quantity: 30240.00,
        unit: 'kgs',
        rate: 3.80,
        amount: 114912.00,
      }
    ],
    
    gstRate: 5,
    gstAmount: 5746.00,
    totalAmount: 120658.00,
    amountInWords: 'ONE LAKHS TWENTY THOUSAND SIX HUNDRED FIFTY EIGHT ONLY',
    
    transport: {
      mode: 'By Lorry',
      vehicleNumber: 'TN18AX9997',
    },
    
    reverseCharge: 'N',
  };

  const formatCurrency = (amount) => {
    return amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
  };

  return (
    <div className="max-w-5xl mx-auto bg-white shadow-2xl rounded-xl overflow-hidden">
      {/* Action Buttons */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-3 flex justify-between items-center print:hidden">
        <h2 className="text-white font-bold text-lg">Tax Invoice</h2>
        <div className="flex gap-2">
          <button
            onClick={onPrint}
            className="flex items-center gap-2 px-4 py-2 bg-white text-amber-600 rounded-lg hover:bg-amber-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={onDownload}
            className="flex items-center gap-2 px-4 py-2 bg-white text-amber-600 rounded-lg hover:bg-amber-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="p-8" id="invoice-content">
        {/* Header */}
        <div className="border-b-4 border-amber-500 pb-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <MJLogo size={64} />
              <div>
                <h1 className="text-3xl font-bold text-slate-800">{data.seller.name}</h1>
                <p className="text-sm text-slate-600 mt-1 max-w-md">{data.seller.address}</p>
                <p className="text-sm font-semibold text-slate-700 mt-2">GSTIN: {data.seller.gstin}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-amber-100 px-4 py-2 rounded-lg inline-block mb-2">
                <p className="text-xs text-amber-700 font-semibold">INVOICE NO.</p>
                <p className="text-lg font-bold text-amber-900">{data.invoiceNumber}</p>
              </div>
              <p className="text-sm text-slate-600">Date: <span className="font-semibold text-slate-800">{data.invoiceDate}</span></p>
            </div>
          </div>
        </div>

        {/* Bill To Section */}
        <div className="mb-6">
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3">BILLED TO</h3>
            <p className="font-bold text-slate-800 text-base mb-2">{data.buyer.name}</p>
            <div className="text-sm text-slate-600 leading-relaxed mb-2">
              {data.buyer.address.split(',').map((line, idx) => (
                <p key={idx}>{line.trim()}{idx < data.buyer.address.split(',').length - 1 ? ',' : ''}</p>
              ))}
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-2">{data.buyer.state}</p>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">GSTIN:</span> {data.buyer.gstin}
            </p>
          </div>
        </div>

        {/* Additional Details */}
        <div className="grid grid-cols-4 gap-4 mb-6 text-sm">
          <div>
            <p className="text-slate-500 text-xs">Date of Supply</p>
            <p className="font-semibold text-slate-800">{data.dateOfSupply}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">PO Number</p>
            <p className="font-semibold text-slate-800">{data.poNumber}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Transport Mode</p>
            <p className="font-semibold text-slate-800">{data.transport.mode}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Vehicle Number</p>
            <p className="font-semibold text-slate-800">{data.transport.vehicleNumber}</p>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-6">
          <table className="w-full border border-slate-300">
            <thead>
              <tr className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                <th className="border border-slate-300 px-3 py-3 text-left text-xs font-bold uppercase">S.No</th>
                <th className="border border-slate-300 px-3 py-3 text-left text-xs font-bold uppercase">Description of Goods</th>
                <th className="border border-slate-300 px-3 py-3 text-right text-xs font-bold uppercase">Quantity</th>
                <th className="border border-slate-300 px-3 py-3 text-right text-xs font-bold uppercase">Rate</th>
                <th className="border border-slate-300 px-3 py-3 text-right text-xs font-bold uppercase">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.lineItems.map((item) => (
                <tr key={item.serialNo} className="hover:bg-slate-50">
                  <td className="border border-slate-300 px-3 py-3 text-slate-700">{item.serialNo}</td>
                  <td className="border border-slate-300 px-3 py-3 font-medium text-slate-800">{item.description}</td>
                  <td className="border border-slate-300 px-3 py-3 text-right text-slate-700">
                    {formatCurrency(item.quantity)} {item.unit}
                  </td>
                  <td className="border border-slate-300 px-3 py-3 text-right text-slate-700">₹{formatCurrency(item.rate)}</td>
                  <td className="border border-slate-300 px-3 py-3 text-right font-semibold text-slate-800">
                    ₹{formatCurrency(item.amount)}
                  </td>
                </tr>
              ))}

              {/* Subtotal */}
              <tr>
                <td colSpan="4" className="border border-slate-300 px-3 py-2 text-right font-semibold text-slate-700">
                  Subtotal
                </td>
                <td className="border border-slate-300 px-3 py-2 text-right font-bold text-slate-800">
                  ₹{formatCurrency(data.lineItems.reduce((sum, item) => sum + item.amount, 0))}
                </td>
              </tr>

              {/* GST */}
              <tr>
                <td colSpan="4" className="border border-slate-300 px-3 py-2 text-right font-semibold text-slate-700">
                  GST @ {data.gstRate}%
                </td>
                <td className="border border-slate-300 px-3 py-2 text-right font-semibold text-amber-600">
                  ₹{formatCurrency(data.gstAmount)}
                </td>
              </tr>

              {/* Total */}
              <tr className="bg-amber-50">
                <td colSpan="4" className="border border-slate-300 px-3 py-3 text-right font-bold text-lg text-slate-800">
                  Total Amount
                </td>
                <td className="border border-slate-300 px-3 py-3 text-right font-bold text-lg text-amber-700">
                  ₹{formatCurrency(data.totalAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Amount in Words */}
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
          <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Amount in Words</p>
          <p className="font-bold text-slate-800 text-base">{data.amountInWords}</p>
        </div>

        {/* Bank Details & Terms */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">Bank Details</h3>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">Bank Name:</span> {data.seller.bankName}
            </p>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">Account No:</span> {data.seller.accountNumber}
            </p>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">Other Details</h3>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">Reverse Charge:</span> {data.reverseCharge}
            </p>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">State:</span> {data.seller.state}
            </p>
          </div>
        </div>

        {/* Footer Signature */}
        <div className="flex justify-end pt-8 border-t-2 border-slate-200">
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-700 mb-16">For {data.seller.name}</p>
            <div className="mb-4 text-slate-400 text-xs italic">
              (Stamp & Signature)
            </div>
            <div className="border-t-2 border-slate-800 pt-2 px-12 inline-block">
              <p className="font-bold text-slate-800 text-sm">Authorized Signatory</p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
          <p>This is a computer generated invoice and does not require a physical signature.</p>
        </div>
      </div>
    </div>
  );
}
